/**
 * Stakeholder Communication & Presentation Utilities
 */

import {
  StakeholderView,
  PresentationSlide,
  ViewLevel,
  AudienceType,
  ExtendedArchiMateDiagram,
} from './extended-types';
import { ArchiMateElement } from './types';
import { uuidv7 } from 'uuidv7';

export function createStakeholderView(
  name: string,
  audience: AudienceType,
  viewLevel: ViewLevel
): StakeholderView {
  // Default layer visibility based on view level
  const visibleLayers = getDefaultVisibleLayers(viewLevel);

  // Default simplifications based on audience
  const simplifications = getDefaultSimplifications(audience);

  return {
    id: uuidv7(),
    name,
    description: `View optimized for ${audience} audience`,
    audience,
    viewLevel,
    visibleLayers,
    hiddenElementTypes: [],
    simplifications,
  };
}

function getDefaultVisibleLayers(viewLevel: ViewLevel): string[] {
  switch (viewLevel) {
    case 'strategic':
      return ['strategy', 'motivation'];
    case 'business':
      return ['business', 'application'];
    case 'technical':
      return ['application', 'technology', 'physical'];
  }
}

function getDefaultSimplifications(audience: AudienceType): StakeholderView['simplifications'] {
  switch (audience) {
    case 'executive':
      return {
        hideRelationshipLabels: true,
        hideEmptyProperties: true,
        groupByCapability: true,
      };
    case 'business':
      return {
        hideRelationshipLabels: false,
        hideEmptyProperties: true,
        groupByCapability: true,
      };
    case 'technical':
      return {
        hideRelationshipLabels: false,
        hideEmptyProperties: false,
        groupByCapability: false,
      };
    case 'mixed':
      return {
        hideRelationshipLabels: false,
        hideEmptyProperties: true,
        groupByCapability: true,
      };
  }
}

export function filterDiagramForView(
  diagram: ExtendedArchiMateDiagram,
  view: StakeholderView
): {
  elements: ArchiMateElement[];
  relationshipIds: string[];
} {
  // Filter elements by layer and type
  const elements = diagram.elements.filter(
    (element) =>
      view.visibleLayers.includes(element.layer) &&
      !view.hiddenElementTypes.includes(element.type)
  );

  // Filter relationships to only include those between visible elements
  const visibleElementIds = new Set(elements.map((e) => e.id));
  const relationships = diagram.relationships.filter(
    (rel) => visibleElementIds.has(rel.sourceId) && visibleElementIds.has(rel.targetId)
  );

  return {
    elements,
    relationshipIds: relationships.map((r) => r.id),
  };
}

export function generateExecutiveSummary(diagram: ExtendedArchiMateDiagram): string {
  let summary = `# Architecture Overview: ${diagram.name}\n\n`;

  // High-level statistics
  const elementsByLayer: Record<string, number> = {};
  diagram.elements.forEach((element) => {
    elementsByLayer[element.layer] = (elementsByLayer[element.layer] || 0) + 1;
  });

  summary += `## System Overview\n\n`;
  summary += `This architecture comprises ${diagram.elements.length} components organized across ${Object.keys(elementsByLayer).length} architectural layers.\n\n`;

  // Key capabilities (strategy layer)
  const capabilities = diagram.elements.filter((e) => e.type === 'capability');
  if (capabilities.length > 0) {
    summary += `## Key Capabilities\n\n`;
    capabilities.slice(0, 5).forEach((cap) => {
      summary += `- **${cap.name}**`;
      if (cap.documentation) {
        summary += `: ${cap.documentation.substring(0, 100)}`;
      }
      summary += `\n`;
    });
    summary += `\n`;
  }

  // Business services
  const businessServices = diagram.elements.filter((e) => e.type === 'business-service');
  if (businessServices.length > 0) {
    summary += `## Business Services\n\n`;
    summary += `The architecture supports ${businessServices.length} business services:\n`;
    businessServices.slice(0, 5).forEach((service) => {
      summary += `- ${service.name}\n`;
    });
    if (businessServices.length > 5) {
      summary += `- ...and ${businessServices.length - 5} more\n`;
    }
    summary += `\n`;
  }

  // Technology summary
  const techElements = diagram.elements.filter((e) => e.layer === 'technology');
  if (techElements.length > 0) {
    summary += `## Technology Footprint\n\n`;
    summary += `${techElements.length} technology components support the architecture.\n\n`;
  }

  return summary;
}

export function generatePresentationSlides(
  diagram: ExtendedArchiMateDiagram
): PresentationSlide[] {
  const slides: PresentationSlide[] = [];

  // Slide 1: Overview
  slides.push({
    id: uuidv7(),
    title: `Architecture Overview: ${diagram.name}`,
    content: generateExecutiveSummary(diagram),
    viewLevel: 'strategic',
    focusElementIds: [],
    speakerNotes: 'Introduce the overall architecture and its purpose',
    order: 1,
  });

  // Slide 2: Strategic View (capabilities)
  const capabilities = diagram.elements.filter(
    (e) => e.layer === 'strategy' || e.type === 'capability'
  );
  if (capabilities.length > 0) {
    slides.push({
      id: uuidv7(),
      title: 'Strategic Capabilities',
      content: `This architecture is built around ${capabilities.length} core capabilities that drive business value.`,
      viewLevel: 'strategic',
      focusElementIds: capabilities.map((e) => e.id),
      speakerNotes: 'Discuss the strategic capabilities and their business value',
      order: 2,
    });
  }

  // Slide 3: Business View
  const businessElements = diagram.elements.filter((e) => e.layer === 'business');
  if (businessElements.length > 0) {
    slides.push({
      id: uuidv7(),
      title: 'Business Architecture',
      content: `The business architecture includes ${businessElements.length} business elements including processes, services, and actors.`,
      viewLevel: 'business',
      focusElementIds: businessElements.slice(0, 10).map((e) => e.id),
      speakerNotes: 'Explain the business processes and how they deliver value',
      order: 3,
    });
  }

  // Slide 4: Application View
  const applicationElements = diagram.elements.filter((e) => e.layer === 'application');
  if (applicationElements.length > 0) {
    slides.push({
      id: uuidv7(),
      title: 'Application Landscape',
      content: `${applicationElements.length} application components support the business capabilities.`,
      viewLevel: 'business',
      focusElementIds: applicationElements.slice(0, 10).map((e) => e.id),
      speakerNotes: 'Overview of key applications and their integration points',
      order: 4,
    });
  }

  // Slide 5: Technology View
  const technologyElements = diagram.elements.filter(
    (e) => e.layer === 'technology' || e.layer === 'physical'
  );
  if (technologyElements.length > 0) {
    slides.push({
      id: uuidv7(),
      title: 'Technology Infrastructure',
      content: `The technology foundation consists of ${technologyElements.length} components.`,
      viewLevel: 'technical',
      focusElementIds: technologyElements.slice(0, 10).map((e) => e.id),
      speakerNotes: 'Technical infrastructure and deployment architecture',
      order: 5,
    });
  }

  return slides;
}

export function simplifyForAudience(
  diagram: ExtendedArchiMateDiagram,
  audience: AudienceType
): ExtendedArchiMateDiagram {
  const view = createStakeholderView(`${audience} View`, audience, getViewLevelForAudience(audience));
  const filtered = filterDiagramForView(diagram, view);

  return {
    ...diagram,
    elements: filtered.elements,
    relationships: diagram.relationships.filter((rel) =>
      filtered.relationshipIds.includes(rel.id)
    ),
  };
}

function getViewLevelForAudience(audience: AudienceType): ViewLevel {
  switch (audience) {
    case 'executive':
      return 'strategic';
    case 'business':
      return 'business';
    case 'technical':
      return 'technical';
    case 'mixed':
      return 'business';
  }
}

export function generateSpeakerNotes(
  diagram: ExtendedArchiMateDiagram,
  slide: PresentationSlide
): string {
  let notes = `# Speaker Notes: ${slide.title}\n\n`;

  if (slide.speakerNotes) {
    notes += `${slide.speakerNotes}\n\n`;
  }

  // Add context about focused elements
  if (slide.focusElementIds.length > 0) {
    notes += `## Key Elements\n\n`;
    slide.focusElementIds.forEach((elementId) => {
      const element = diagram.elements.find((e) => e.id === elementId);
      if (element) {
        notes += `- **${element.name}** (${element.type})`;
        if (element.documentation) {
          notes += `\n  ${element.documentation.substring(0, 200)}`;
        }
        notes += `\n`;
      }
    });
  }

  return notes;
}

export function exportToPowerPoint(
  diagram: ExtendedArchiMateDiagram,
  slides: PresentationSlide[]
): string {
  // Generate markdown that can be converted to PowerPoint
  let pptx = '';

  slides.forEach((slide) => {
    pptx += `---\n\n`;
    pptx += `# ${slide.title}\n\n`;
    pptx += `${slide.content}\n\n`;

    if (slide.focusElementIds.length > 0) {
      pptx += `<!-- Focus: ${slide.focusElementIds.join(', ')} -->\n\n`;
    }

    if (slide.speakerNotes) {
      pptx += `:::: notes\n${slide.speakerNotes}\n::::\n\n`;
    }
  });

  return pptx;
}

export function generateInteractiveHTML(diagram: ExtendedArchiMateDiagram): string {
  // Generate standalone HTML with embedded diagram data
  const html = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${diagram.name} - Interactive Architecture</title>
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      margin: 0;
      padding: 20px;
      background: #0A1E2E;
      color: #E8D5B7;
    }
    .container {
      max-width: 1400px;
      margin: 0 auto;
    }
    h1 {
      color: #E89841;
    }
    .canvas {
      background: white;
      border-radius: 8px;
      padding: 20px;
      margin: 20px 0;
    }
    .element {
      cursor: pointer;
      transition: all 0.2s;
    }
    .element:hover {
      opacity: 0.8;
    }
    .sidebar {
      position: fixed;
      right: 0;
      top: 0;
      width: 300px;
      height: 100vh;
      background: #1a2e3e;
      padding: 20px;
      overflow-y: auto;
      box-shadow: -2px 0 10px rgba(0,0,0,0.3);
    }
    .sidebar h2 {
      color: #E89841;
      margin-top: 0;
    }
    .sidebar .detail {
      margin-bottom: 15px;
    }
    .sidebar .label {
      font-weight: bold;
      color: #E89841;
    }
  </style>
</head>
<body>
  <div class="container">
    <h1>${diagram.name}</h1>
    <div class="canvas" id="canvas">
      <!-- Canvas content would be rendered here -->
      <p>Interactive diagram visualization</p>
    </div>
  </div>

  <div class="sidebar" id="sidebar">
    <h2>Element Details</h2>
    <p>Click on an element to see details</p>
  </div>

  <script>
    // Diagram data
    const diagramData = ${JSON.stringify(diagram, null, 2)};

    // Interactive behavior would be implemented here
    console.log('Diagram loaded:', diagramData.name);
  </script>
</body>
</html>
  `;

  return html;
}

export function generateVideoNarration(
  diagram: ExtendedArchiMateDiagram,
  slides: PresentationSlide[]
): string {
  let narration = `# Architecture Walkthrough Script\n\n`;
  narration += `## ${diagram.name}\n\n`;

  slides.forEach((slide, index) => {
    narration += `### Slide ${index + 1}: ${slide.title}\n\n`;
    narration += `**[Show: ${slide.viewLevel} view]**\n\n`;
    narration += `${slide.speakerNotes}\n\n`;

    if (slide.focusElementIds.length > 0) {
      narration += `**[Highlight: ${slide.focusElementIds.length} key elements]**\n\n`;
    }

    narration += `---\n\n`;
  });

  return narration;
}

export const PRESENTATION_TEMPLATES = {
  'executive-briefing': {
    name: 'Executive Briefing',
    duration: '15 minutes',
    slides: ['overview', 'strategic-capabilities', 'business-value', 'roadmap'],
    viewLevel: 'strategic' as ViewLevel,
  },
  'technical-deep-dive': {
    name: 'Technical Deep Dive',
    duration: '45 minutes',
    slides: [
      'overview',
      'application-landscape',
      'technology-stack',
      'integrations',
      'deployment',
      'security',
    ],
    viewLevel: 'technical' as ViewLevel,
  },
  'business-review': {
    name: 'Business Review',
    duration: '30 minutes',
    slides: ['overview', 'business-processes', 'services', 'applications', 'costs', 'roadmap'],
    viewLevel: 'business' as ViewLevel,
  },
  'architecture-decision': {
    name: 'Architecture Decision Meeting',
    duration: '60 minutes',
    slides: [
      'current-state',
      'problems',
      'proposed-solution',
      'scenarios',
      'comparison',
      'recommendation',
    ],
    viewLevel: 'business' as ViewLevel,
  },
};

// Aliases for backward compatibility
export const exportStakeholderView = exportToPowerPoint;
export const generateStakeholderPresentation = generatePresentationSlides;
